/**
 * Enhanced Theme Provider
 *
 * Provides global theme context with direct access to theme values
 * Best architecture: Single source of truth with type-safe access
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getTheme, type Theme, type ThemeMode } from './theme'
import { applyThemeToDocument } from './utils'

interface ThemeContextValue {
  // Current theme mode
  mode: ThemeMode

  // Toggle theme function
  toggleTheme: () => void

  // Set theme mode directly
  setTheme: (mode: ThemeMode) => void

  // Direct access to current theme object
  theme: Theme

  // Check if dark mode
  isDark: boolean

  // Check if light mode
  isLight: boolean
}

// exporté pour les racines React détachées (pop-ups Mapbox), hors de l'arbre applicatif.
export const ThemeContext = createContext<ThemeContextValue | undefined>(
  undefined,
)

interface ThemeProviderProps {
  children: React.ReactNode
  defaultTheme?: ThemeMode
  storageKey?: string
  persistTheme?: boolean
}

/**
 * Enhanced Theme Provider Component
 *
 * Features:
 * - Global theme context accessible everywhere
 * - Automatic theme persistence (optional)
 * - CSS variable injection for Tailwind support
 * - Type-safe theme access
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  storageKey = 'elda-theme',
  persistTheme = true,
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    let resolved: ThemeMode = defaultTheme
    if (persistTheme && typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey) as ThemeMode | null
      if (stored === 'light' || stored === 'dark') {
        resolved = stored
      } else {
        const oldTheme = localStorage.getItem('dark-theme')
        if (oldTheme !== null) {
          try {
            const oldValue = JSON.parse(oldTheme)
            resolved = oldValue ? 'dark' : 'light'
            localStorage.setItem(storageKey, resolved)
            localStorage.removeItem('dark-theme')
          } catch {
            // ignore
          }
        }
      }
    }
    applyThemeToDocument(resolved)
    return resolved
  })

  // Get current theme object
  const theme = useMemo(() => getTheme(mode), [mode])

  // Toggle theme function
  const toggleTheme = () => {
    setMode(prev => (prev === 'light' ? 'dark' : 'light'))
  }

  // Set theme directly
  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode)
  }

  // Persist theme to localStorage
  useEffect(() => {
    if (persistTheme && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, mode)
    }
  }, [mode, storageKey, persistTheme])

  // Keep document in sync when mode changes (e.g. user toggles theme)
  useEffect(() => {
    applyThemeToDocument(mode)
  }, [mode])

  const value: ThemeContextValue = useMemo(
    () => ({
      mode,
      toggleTheme,
      setTheme,
      theme,
      isDark: mode === 'dark',
      isLight: mode === 'light',
    }),
    [mode, theme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * Hook to access theme context
 *
 * @throws Error if used outside ThemeProvider
 *
 * @example
 * ```tsx
 * const { theme, mode, toggleTheme } = useTheme()
 * const bgColor = theme.button.primary.bg
 * ```
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

/**
 * Hook to get theme mode only
 *
 * @example
 * ```tsx
 * const mode = useThemeMode() // 'light' | 'dark'
 * ```
 */
export const useThemeMode = (): ThemeMode => {
  const { mode } = useTheme()
  return mode
}

/**
 * Hook to get current theme object
 *
 * @example
 * ```tsx
 * const theme = useThemeObject()
 * const bgColor = theme.button.primary.bg
 * ```
 */
export const useThemeObject = (): Theme => {
  const { theme } = useTheme()
  return theme
}

/**
 * Hook to toggle theme
 *
 * @example
 * ```tsx
 * const toggleTheme = useToggleTheme()
 * <button onClick={toggleTheme}>Toggle Theme</button>
 * ```
 */
export const useToggleTheme = () => {
  const { toggleTheme } = useTheme()
  return toggleTheme
}

/**
 * Safe variant of useTheme that falls back to a light-mode stub when no
 * ThemeProvider is mounted (e.g. unit-test environments).
 *
 * Prefer useTheme in production code; use useSafeTheme only in components
 * that must remain testable without a provider wrapper.
 */
export const useSafeTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (!context) {
    const fallbackTheme = getTheme('light')
    return {
      mode: 'light',
      toggleTheme: () => undefined,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setTheme: (_mode: ThemeMode) => undefined,
      theme: fallbackTheme,
      isDark: false,
      isLight: true,
    }
  }
  return context
}

export default ThemeProvider
